import { useContext, useLayoutEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPhotoViewer, type PhotoViewer } from '../../shared/photo-viewer.js';
import { holdKeyboardForModal } from '../state/modal-stack.ts';
import { SessionContext, offlineAuthorizationFor } from '../state/session.tsx';

let viewer: PhotoViewer | undefined;

/** Retire an open gallery when its source receives a definitive read denial. */
export function closePhotoViewer() {
  viewer?.close();
}

/** Close synchronously when the route or authorization boundary changes. */
export function PhotoViewerBoundary() {
  const session = useContext(SessionContext);
  const route = useLocation();
  const scope = JSON.stringify([
    route.pathname,
    route.search,
    route.hash,
    session?.user?.id,
    session?.company?.id,
    session?.currentLocation?.id,
    session?.impersonatedBy,
    session?.user && session.company ? offlineAuthorizationFor(session.user, session.company) : null,
  ]);
  useLayoutEffect(() => () => viewer?.close(), [scope]);
  return null;
}

/** Explicit opt-in, with groups limited to the gallery the caller names. */
export function PhotoTrigger({
  src,
  alt,
  group,
  notice,
  filename,
  contentType,
  className,
  children,
}: {
  src: string;
  alt: string;
  group?: string;
  notice?: string;
  filename?: string;
  contentType?: string;
  className?: string;
  children: ReactNode;
}) {
  const { i18n } = useTranslation();
  const es = i18n.language.startsWith('es');
  return (
    <button
      type="button"
      className={className}
      data-photo-src={src}
      data-photo-alt={alt}
      data-photo-group={group}
      data-photo-notice={notice}
      data-photo-filename={filename}
      data-photo-content-type={contentType}
      aria-label={`${es ? 'Ampliar foto' : 'Enlarge photo'}: ${alt}`}
      onClick={(event) => {
        viewer?.close();
        viewer = createPhotoViewer(i18n.language, undefined, holdKeyboardForModal);
        const trigger = event.currentTarget;
        const nodes = group
          ? [...document.querySelectorAll<HTMLButtonElement>('[data-photo-group]')].filter(
              (node) => node.dataset.photoGroup === group,
            )
          : [trigger];
        viewer.open(
          nodes.map((node) => ({
            src: node.dataset.photoSrc!,
            alt: node.dataset.photoAlt,
            notice: node.dataset.photoNotice,
            filename: node.dataset.photoFilename,
            contentType: node.dataset.photoContentType,
          })),
          nodes.indexOf(trigger),
          trigger,
        );
      }}
    >
      {children}
    </button>
  );
}
